import { useEffect, useState } from "react";
import ModalComponent from "components/modal";
import { addPosition, changePositionStatus } from "api/position";
import { useSharedState } from "state";
import Table from "components/table";
import ActionButton from "components/table/action-btn";
import { oneLine } from "components/table/table-parts";
import { IconCheck, IconLock, IconLockOpen, IconPencil, IconX } from "@tabler/icons-react";
import { Position, PositionDefault } from "types/dict";
import WarningModal from "components/modal/warning";

export default function Positions() {
  const { positions, getPositionsData, notify } = useSharedState();

  const [positionToChange, setPositionToChange] = useState<Position>(PositionDefault);
  const [editingPosition, setEditingPosition] = useState<number>(0);
  const [isAddingPosition, setAddingPosition] = useState<boolean>(false);
  const [isFormLoading, setFormLoading] = useState<boolean>(false);

  const [newPositionName, setNewPositionName] = useState<string>("");
  const [newPositionAccessInfo, setNewPositionAccessInfo] = useState<boolean>(false);
  const [newPositionMinSum, setNewPositionMinSum] = useState<number | undefined>(undefined);

  const ChangePositionDanger = "Вы действительно хотите изменить информацию о должности?";
  const ChangeStatusDanger = "Вы действительно хотите изменить статус должности?";

  useEffect(() => {
    getPositionsData();
  }, []);

  const openPositionModal = () => {
    const button = document.createElement("button");
    button.setAttribute("data-bs-toggle", "modal");
    button.setAttribute("data-bs-target", "#modal");
    document.body.appendChild(button);
    button.click();
    button.remove();
  };

  const resetInputs = () => {
    setNewPositionName("");
    setNewPositionMinSum(0);
    setNewPositionAccessInfo(false);
  };

  const cancelPositionChange = () => {
    setPositionToChange(PositionDefault);
    setEditingPosition(0);
    setAddingPosition(false);

    resetInputs();
  };

  const onPositionEdit = (item: Position) => {
    setEditingPosition(item.position_id);
    setPositionToChange(item);
    setAddingPosition(false);

    setNewPositionName(item.position);
    setNewPositionMinSum(item.min_sum);
    setNewPositionAccessInfo(item.access_to_additional_info);
  };

  const onPositionBlock = (item: Position) => {
    setPositionToChange(item);
    setEditingPosition(0);
    setAddingPosition(false);

    resetInputs();
  };

  const onPositionCreate = () => {
    setEditingPosition(0);
    setAddingPosition(true);

    resetInputs();
  };

  const onChangePosition = async (status: boolean) => {
    openPositionModal();
    setFormLoading(true);

    try {
      await changePositionStatus(
        positionToChange.position_id,
        status ? !positionToChange.status : positionToChange.status,
        newPositionAccessInfo || positionToChange.access_to_additional_info,
        status ? positionToChange.min_sum : newPositionMinSum || 0
      );
      notify("Статус изменён", "success");

      getPositionsData();
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при смене статуса");
    } finally {
      setFormLoading(false);
      cancelPositionChange();
    }
  };

  const createPosition = async () => {
    setFormLoading(true);

    try {
      await addPosition(newPositionName, newPositionAccessInfo || false, newPositionMinSum || 0);
      notify("Должность добавлена", "success");

      cancelPositionChange();
      getPositionsData();
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при добавлении должности");
    } finally {
      setFormLoading(false);
    }
  };

  const positionsTableHeaders = [
    { header_text: "Должность", narrow: false, wrap: false },
    { header_text: "Мин. сумма", narrow: false, wrap: false },
    { header_text: "Доп. инфо", narrow: false, wrap: false },
    { header_text: "Статус", narrow: false, wrap: false },
    { header_text: "Действия", narrow: false, wrap: false },
  ];

  const positionsList = (
    <>
      {positions
        .filter(
          (pos) =>
            pos.position.toLocaleLowerCase().trim() !== "менеджер сотрудника" &&
            pos.position.toLocaleLowerCase().trim() !== "создатель"
        )
        .map((position, index) => (
          <tr key={index}>
            <td>{oneLine(position.position)}</td>

            {editingPosition === position.position_id ? (
              <>
                <td>
                  <input
                    type="number"
                    className="form-control rounded-2xl"
                    placeholder="Мин. сумма"
                    value={newPositionMinSum}
                    onChange={(e) => setNewPositionMinSum(Number(e.target.value))}
                    disabled={isFormLoading}
                  />
                </td>
                <td>
                  <input
                    type="checkbox"
                    className="form-check-input h-5 w-5"
                    checked={newPositionAccessInfo}
                    onChange={(e) => setNewPositionAccessInfo(e.target.checked)}
                    disabled={isFormLoading}
                  />
                </td>
                <td></td>
                <td>
                  <div className="btn-list flex-nowrap">
                    <ActionButton
                      icon={<IconCheck className="icon" />}
                      title="Сохранить"
                      action={() => undefined}
                      color="primary"
                      modal={true}
                      modal_id="#modal-change-position"
                    />
                    <ActionButton
                      icon={<IconX className="icon" />}
                      title="Отмена"
                      action={cancelPositionChange}
                      color="danger"
                      modal={false}
                    />
                  </div>
                </td>
              </>
            ) : (
              <>
                <td>{position.min_sum || 0}</td>
                <td>{position.access_to_additional_info ? "Да" : "Нет"}</td>
                <td>{oneLine(position.status ? "Активный" : "Неактивный", position.status ? "primary" : "danger")}</td>
                <td>
                  <div className="btn-list flex-nowrap">
                    <ActionButton
                      icon={<IconPencil className="icon" />}
                      title="Изменить"
                      action={() => onPositionEdit(position)}
                      color="primary"
                      modal={false}
                    />
                    <ActionButton
                      icon={position.status ? <IconLock className="icon" /> : <IconLockOpen className="icon" />}
                      title={`${position.status ? "Деактивировать" : "Активировать"}`}
                      action={() => onPositionBlock(position)}
                      color={`${position.status ? "danger" : "primary"}`}
                      modal={true}
                      modal_id="#modal-block-position"
                    />
                  </div>
                </td>
              </>
            )}
          </tr>
        ))}

      {isAddingPosition && (
        <tr>
          <td>
            <input
              type="text"
              className="form-control rounded-2xl"
              placeholder="Название новой должности"
              value={newPositionName}
              onChange={(e) => setNewPositionName(e.target.value.slice(0, 100))}
              disabled={isFormLoading}
            />
          </td>
          <td>
            <input
              type="number"
              className="form-control rounded-2xl"
              placeholder="Мин. сумма"
              value={newPositionMinSum}
              onChange={(e) => setNewPositionMinSum(Number(e.target.value))}
              disabled={isFormLoading}
            />
          </td>
          <td>
            <input
              type="checkbox"
              className="form-check-input h-5 w-5"
              checked={newPositionAccessInfo}
              onChange={(e) => setNewPositionAccessInfo(e.target.checked)}
              disabled={isFormLoading}
            />
          </td>
          <td></td>
          <td>
            <ActionButton
              icon={<IconX className="icon" />}
              title="Отмена"
              action={cancelPositionChange}
              color="danger"
              modal={false}
            />
          </td>
        </tr>
      )}
    </>
  );

  const usersTable = Table(positionsTableHeaders, positionsList, false);

  const positionsFooter = (
    <button
      className="btn btn-primary d-flex gap-1"
      onClick={isAddingPosition ? createPosition : onPositionCreate}
      disabled={isFormLoading || (isAddingPosition && !newPositionName?.length)}
    >
      {isAddingPosition ? "Сохранить" : "Добавить должность"}
      {isFormLoading && <div className="spinner-border spinner-border-sm" role="status" />}
    </button>
  );

  return (
    <>
      <ModalComponent
        header="Список должностей"
        body={usersTable}
        footer={positionsFooter}
        disableClose={isFormLoading}
      />
      <WarningModal
        modal_id="modal-change-position"
        warningDescription={ChangePositionDanger}
        cancel={openPositionModal}
        confirm={() => onChangePosition(false)}
      />
      <WarningModal
        modal_id="modal-block-position"
        warningDescription={ChangeStatusDanger}
        cancel={openPositionModal}
        confirm={() => onChangePosition(true)}
      />
    </>
  );
}
